/**
 * 📊 SEO-GEO RAPORU — ajans sunum formatında rapor ekranı + yazdırılabilir çıktı.
 *
 * Manifesto (klasik ajans raporu):
 *   1. Site İçi SEO   — sağlık skoru, taranan sayfa, link durumları, hata/uyarı/bildirim tablosu
 *   2. Site Hızı      — Lighthouse performans + Core Web Vitals
 *   3. Site Dışı SEO  — domain skoru, backlink, referans domain, toxic dağılımı, anchor'lar
 *   4. GEO-AEO        — AI görünürlük, motor kırılımı, AI bot erişimi, anılan markalar
 *   5. Rakip Analizi  — organik rakipler kıyas tablosu
 *
 * Kaynak: kayıtlı site (mevcut ajan verilerinden anında) veya dış/rastgele domain (canlı tarama).
 * "Raporu İndir" = window.print() → tarayıcının PDF çıktısı (print CSS shell'i gizler).
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useToast } from "@/hooks/use-toast";
import { confirmCost } from "@/components/cost-confirm";
import { GlPageHeader, GlSection, GlEmpty, GlButton } from "@/components/ui/gl-kit";
import { ReportBody, type ReportData } from "@/components/report-body";
import { GlTableWrap, GlTable, GlTHead, GlTBody, GlTr, GlTh, GlTd, GlBadge } from "@/components/ui/gl-table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileChartColumn, Loader2, Globe, Trash2, ShieldAlert, Share2 } from "lucide-react";

// ── Tipler (SeoReportData aynası) ────────────────────────────────────────────
// ReportData + gövde artık components/report-body.tsx'te (paylaşımlı sayfayla ortak)

type Report = { id: number; target: string; domainId: number | null; market: string; status: string; stage: string; error?: string | null; data: ReportData | null; createdAt: string };

export default function SeoReport() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const { toast } = useToast();
  const qc = useQueryClient();

  const [mode, setMode] = useState<"registered" | "external">("registered");
  const [pickedDomain, setPickedDomain] = useState<number | null>(null);
  const [extTarget, setExtTarget] = useState("");
  const [market, setMarket] = useState("tr");
  const [compInput, setCompInput] = useState("");   // elle rakip listesi (boş = otomatik öner)
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [shareBusy, setShareBusy] = useState(false);
  const pollRef = useRef<number | null>(null);

  const domainId = pickedDomain ?? activeDomainId;

  const { data: listData } = useQuery<{ reports: Report[] }>({
    queryKey: ["seo-reports"],
    queryFn: async () => (await fetch("/api/seo-reports")).json(),
  });

  const { data: openData } = useQuery<{ report: Report }>({
    queryKey: ["seo-report", openId],
    enabled: openId != null,
    refetchInterval: (q) => (q.state.data?.report.status === "running" ? 5000 : false),
    queryFn: async () => {
      const r = await fetch(`/api/seo-reports/${openId}`);
      if (!r.ok) throw new Error("Rapor okunamadı");
      return r.json();
    },
  });

  useEffect(() => () => { if (pollRef.current) window.clearInterval(pollRef.current); }, []);

  const shareReport = async (id: number) => {
    setShareBusy(true);
    try {
      const res = await fetch(`/api/seo-reports/${id}/share`, { method: "POST", headers: { "content-type": "application/json" }, body: "{}" });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error ?? "Paylaşılamadı");
      const url = `${window.location.origin}/r/${j.shareToken}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast({ title: "Paylaşım linki kopyalandı", description: "Müşterin bu linki girişsiz açabilir — ajans markan görünür. Linki kapatmak için tekrar Paylaş menüsünü kullan." });
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setShareBusy(false); }
  };

  const generate = async () => {
    const isExternal = mode === "external";
    if (isExternal && !extTarget.trim()) { toast({ title: "Domain gir", variant: "destructive" }); return; }
    if (!isExternal && !domainId) { toast({ title: "Site seç", variant: "destructive" }); return; }
    if (!(await confirmCost(isExternal ? "seo-report-live" : "seo-report"))) return;
    setBusy(true);
    try {
      const r = await fetch("/api/seo-reports", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify(isExternal ? { target: extTarget.trim(), market, competitors: compInput } : { domainId, market, competitors: compInput }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Rapor başlatılamadı");
      qc.invalidateQueries({ queryKey: ["seo-reports"] });
      setOpenId(j.report.id);
      toast({ title: "Rapor hazırlanıyor", description: "Hız, AI Overview ve rakip verileri canlı ölçülüyor — ekran kendini günceller." });
    } catch (e) {
      toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" });
    } finally { setBusy(false); }
  };

  const removeReport = async (id: number) => {
    await fetch(`/api/seo-reports/${id}`, { method: "DELETE" });
    if (openId === id) setOpenId(null);
    qc.invalidateQueries({ queryKey: ["seo-reports"] });
  };

  const rep = openData?.report;
  const d = rep?.data ?? null;

  return (
    <div className="mx-auto max-w-[1280px] p-4 md:p-6">
      {/* ── Kontroller (yazdırmada gizli) ── */}
      <div className="print:hidden">
        <GlPageHeader
          title="SEO-GEO Raporu"
          desc="Kayıtlı sitenin ajan verilerinden anında rapor derle ya da dışarıdan herhangi bir domaini taratıp aynı formatta rapor al."
          icon={<FileChartColumn className="h-5 w-5" />}
        />

        <GlSection className="mb-5">
          <div className="grid gap-3 lg:grid-cols-[auto_1fr_auto_auto] lg:items-center">
            <div className="inline-flex items-center gap-0.5 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800">
              {([["registered", "Kayıtlı site"], ["external", "Dış domain"]] as const).map(([k, l]) => (
                <button key={k} onClick={() => setMode(k)}
                  className={`rounded-md px-3 py-1.5 text-theme-sm font-medium transition ${mode === k ? "bg-white text-gray-800 shadow-theme-xs dark:bg-white/10 dark:text-white/90" : "text-gray-500 hover:text-gray-700 dark:text-gray-400"}`}>
                  {l}
                </button>
              ))}
            </div>

            {mode === "registered" ? (
              <Select value={domainId ? String(domainId) : ""} onValueChange={(v) => setPickedDomain(parseInt(v))}>
                <SelectTrigger className="h-10 w-full lg:w-80"><SelectValue placeholder="Site seç" /></SelectTrigger>
                <SelectContent>
                  {(domains ?? []).map((dm) => (
                    <SelectItem key={dm.id} value={String(dm.id)}>{dm.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <input
                value={extTarget}
                onChange={(e) => setExtTarget(e.target.value)}
                placeholder="rakipsite.com"
                className="h-10 w-full rounded-lg border border-gray-300 bg-white px-3.5 text-theme-sm text-gray-800 outline-none placeholder:text-gray-400 focus:border-brand-300 dark:border-gray-700 dark:bg-white/[0.03] dark:text-white/90 lg:w-80"
              />
            )}

            <Select value={market} onValueChange={setMarket}>
              <SelectTrigger className="h-10 w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tr">Türkçe (TR)</SelectItem>
                <SelectItem value="en">İngilizce (US)</SelectItem>
                <SelectItem value="de">Almanca (DE)</SelectItem>
                <SelectItem value="ar">Arapça (SA)</SelectItem>
              </SelectContent>
            </Select>

            <GlButton onClick={generate} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileChartColumn className="h-4 w-4" />}
              Rapor Oluştur
            </GlButton>
          </div>

          <div className="mt-3 grid gap-2 lg:grid-cols-[auto_1fr] lg:items-center">
            <span className="text-theme-xs font-medium text-gray-600 dark:text-gray-300">Rakipler (isteğe bağlı):</span>
            <input
              value={compInput}
              onChange={(e) => setCompInput(e.target.value)}
              placeholder="rakip1.com, rakip2.com — boş bırakırsan otomatik önerilir"
              className="h-9 w-full rounded-lg border border-gray-300 bg-white px-3 text-theme-xs text-gray-800 outline-none placeholder:text-gray-400 focus:border-brand-300 dark:border-gray-700 dark:bg-white/[0.03] dark:text-white/90"
            />
          </div>
          <p className="mt-2 text-theme-xs text-gray-500">
            {mode === "external"
              ? "Dış domain raporu siteyi baştan tarar (100 sayfaya kadar). Hız, AI Overview ve rakip trendi her raporda canlı ölçülür — birkaç dakika sürer."
              : "Kayıtlı site raporu ajan verilerini kullanır; hız (mobil+masaüstü), AI Overview ve rakip trendi canlı ölçülür — 1-2 dakika sürer."}
          </p>
        </GlSection>

        {/* Geçmiş raporlar */}
        {!!listData?.reports.length && (
          <GlSection title="Raporlar" bodyClass="!px-0 !py-0" className="mb-5">
            <GlTableWrap>
              <GlTable>
                <GlTHead>
                  <GlTr><GlTh>Hedef</GlTh><GlTh>Kaynak</GlTh><GlTh>Durum</GlTh><GlTh>Tarih</GlTh><GlTh /></GlTr>
                </GlTHead>
                <GlTBody>
                  {listData.reports.map((r) => (
                    <GlTr key={r.id} onClick={() => setOpenId(r.id)} className={openId === r.id ? "bg-brand-25 dark:bg-brand-500/[0.06]" : ""}>
                      <GlTd><span className="font-medium text-gray-800 dark:text-white/90">{r.target}</span></GlTd>
                      <GlTd>{r.domainId ? <GlBadge color="primary">kayıtlı site</GlBadge> : <GlBadge color="light">dış domain</GlBadge>}</GlTd>
                      <GlTd>
                        {r.status === "done" ? <GlBadge color="success">hazır</GlBadge>
                          : r.status === "error" ? <GlBadge color="error">hata</GlBadge>
                          : <GlBadge color="warning"><Loader2 className="h-3 w-3 animate-spin" /> {r.stage || "hazırlanıyor"}</GlBadge>}
                      </GlTd>
                      <GlTd><span className="text-theme-xs text-gray-500">{new Date(r.createdAt).toLocaleString("tr-TR")}</span></GlTd>
                      <GlTd align="right">
                        <button onClick={(e) => { e.stopPropagation(); removeReport(r.id); }} className="text-gray-400 hover:text-error-500" title="Sil">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </GlTd>
                    </GlTr>
                  ))}
                </GlTBody>
              </GlTable>
            </GlTableWrap>
          </GlSection>
        )}
      </div>

      {/* ── RAPOR GÖVDESİ ── */}
      {rep?.status === "running" && (
        <GlEmpty title="Rapor hazırlanıyor" desc={`Aşama: ${rep.stage || "başlatılıyor"} — ekran kendini günceller.`} icon={<Loader2 className="h-6 w-6 animate-spin" />} />
      )}
      {rep?.status === "error" && (
        <GlEmpty title="Rapor üretilemedi" desc={rep.error ?? "Bilinmeyen hata"} icon={<ShieldAlert className="h-6 w-6" />} />
      )}

      {d && rep?.status === "done" && (
        <div>
          <div className="mb-3 flex justify-end print:hidden">
            <GlButton variant="outline" size="sm" onClick={() => rep && shareReport(rep.id)} disabled={shareBusy}>
              {shareBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
              Müşteriyle Paylaş (link)
            </GlButton>
          </div>
          <div id="rapor-govde">
            <ReportBody d={d} />
          </div>
        </div>
      )}

      {!rep && !listData?.reports.length && (
        <GlEmpty
          title="Henüz rapor yok"
          desc="Üstteki formdan kayıtlı bir siteni seç ya da dışarıdan bir domain yazıp ilk raporunu oluştur."
          icon={<Globe className="h-6 w-6" />}
        />
      )}
    </div>
  );
}
